/**
 * Rewards Service
 * Handles API calls for rewards and claims management
 */

import { getAuthToken } from '../utils/tokenManager';
import { API_BASE_URL } from '../config/api';

export interface RewardClaimResponse {
  id: string;
  customer_name: string;
  customer_email?: string;
  campaign_id?: string;
  campaign_name?: string;
  reward_type: string;
  reward_value: string;
  reward_description?: string;
  claimed_at: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface GetRewardClaimsResponse {
  success: boolean;
  data?: {
    claims: RewardClaimResponse[];
    total: number;
    stats?: {
      pending_claims: number;
      approved_claims: number;
      paid_claims: number;
      rejected_claims: number;
      total_payout_amount: number;
    };
  };
  message?: string;
}

/**
 * Get all reward claims
 * @returns List of reward claims
 */
export const getRewardClaims = async (): Promise<GetRewardClaimsResponse> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  console.log('Fetching reward claims...');

  const response = await fetch(
    `${API_BASE_URL.PROFILE}/rewards/claims`,
    {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to fetch reward claims with status ${response.status}`
    );
  }

  const result: GetRewardClaimsResponse = await response.json();
  
  console.log('Reward claims loaded:', result);
  return result;
};

/**
 * Approve a reward claim
 * @param claimId - The claim ID to approve
 */
export const approveRewardClaim = async (claimId: string): Promise<{ success: boolean; message?: string }> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  console.log('Approving claim:', claimId);

  const response = await fetch(
    `${API_BASE_URL.PROFILE}/rewards/claims/${claimId}/approve`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to approve claim with status ${response.status}`
    );
  }

  return await response.json();
};

/**
 * Reject a reward claim
 * @param claimId - The claim ID to reject
 */
export const rejectRewardClaim = async (claimId: string): Promise<{ success: boolean; message?: string }> => {
  const token = getAuthToken();
  
  if (!token) {
    throw new Error('Authentication token not found. Please log in again.');
  }

  console.log('Rejecting claim:', claimId);

  const response = await fetch(
    `${API_BASE_URL.PROFILE}/rewards/claims/${claimId}/reject`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `Failed to reject claim with status ${response.status}`
    );
  }

  return await response.json();
};

